/**
 * FAMILY SUPPORT REGISTRY — MeesterJay 4.1 (Canonical)
 * MOVED from src/config/familySupportRegistry.ts (Phase L-3)
 *
 * The authoritative source for support content (hints, visuals, explanations)
 * keyed by canonical familyId.
 */
import { HintStep } from './hints'
import { VisualAid } from '../visuals/types'
import { requiresVisual } from './visualRequiredRules'
import { debug, warn, error as logError } from '../utils/logger'
export interface RepresentationSupport {
  hints?: HintStep[]
  visual?: VisualAid
  explanation?: string
}
export interface FamilySupportEntry {
  familyId: string
  hints?: HintStep[]
  defaultVisual?: VisualAid
  defaultExplanation?: string
  representations?: Record<string, RepresentationSupport>
  /** Whether this family conceptually requires a visual aid */
  requiresVisual?: boolean
  /** Whether this family conceptually requires hints */
  requiresHints?: boolean
}

/**
 * FAMILY SUPPORT REGISTRY — MeesterJay 4.1 (Canonical)
 *
 * The authoritative source for support content (hints, visuals, explanations)
 * keyed by canonical familyId.
 *
 * NOW INCLUDES INLINE HINTS (No runtime migration needed).
 */
export const FAMILY_SUPPORT_REGISTRY: Record<string, FamilySupportEntry> = {
  // ══════════════════════════════════════════════════════════
  // MODULE: Spelling Werkwoorden (SWW) — 14 families
  // ══════════════════════════════════════════════════════════

  'SWW-TT-STAM': {
    familyId: 'SWW-TT-STAM',
    defaultExplanation:
      'De stam van een werkwoord vind je door het hele werkwoord te nemen en -en eraf te halen. Let op: soms moet je de klinker verdubbelen (lopen → loop) of een medeklinker weglaten (rennen → ren).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel',
      },
      {
        title: 'Let op!',
        content:
          'Soms moet je de klinker verdubbelen:\nlopen - en = lop → loop (open lettergreep)\nspelen - en = spel → speel',
      },
    ],
  },
  'SWW-TT-INV-JIJ': {
    familyId: 'SWW-TT-INV-JIJ',
    defaultExplanation:
      'Bij inversie (omkering) staat "jij" achter het werkwoord. Dan valt de -t weg: "Jij loopt" → "Loop jij?" Let op: dit geldt alleen voor jij/je, niet voor hij/zij.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Jij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"\n"Werk je vandaag?"\n"Vind jij dat ook?"',
      },
      {
        title: 'Vergelijk',
        content:
          'Jij loopt (jij ervoor → +t)\nLoop jij? (jij erachter → geen t)',
      },
    ],
  },
  'SWW-VT-STERK': {
    familyId: 'SWW-VT-STERK',
    defaultExplanation:
      'Sterke werkwoorden veranderen van klank in de verleden tijd: lopen → liep, schrijven → schreef. Ze krijgen GEEN -te of -de. Deze vormen moet je uit je hoofd leren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Wat zijn sterke werkwoorden?',
        content:
          'Bij sterke werkwoorden verandert de klinker:\nlopen → liep\nzingen → zong\nhelpen → hielp\nschrijven → schreef',
      },
      {
        title: 'Geen -te of -de!',
        content:
          'Sterke werkwoorden krijgen GEEN -te of -de.\nNIET: loopte, zingde\nWEL: liep, zong',
      },
    ],
  },
  'SWW-VT-ZWAK-KOF': {
    familyId: 'SWW-VT-ZWAK-KOF',
    defaultExplanation:
      "Gebruik 't kofschip om te bepalen of de verleden tijd op -te(n) of -de(n) eindigt. Eindigt de stam op t, k, f, s, ch of p? Dan +te(n). Anders +de(n).",
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)",
      },
    ],
  },
  'SWW-VD-KOF': {
    familyId: 'SWW-VD-KOF',
    defaultExplanation:
      'Het voltooid deelwoord: ge + stam + t (bij kofschip) of ge + stam + d (geen kofschip). Voorbeeld: werken → gewerkt, leven → geleefd.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          "Stam eindigt op 't kofschip letter?\n→ ge + stam + t\n\nVoorbeelden:\nwerk → gewerkt (K)\ndans → gedanst (S)\npost → gepost (T, al een t dus geen extra)",
      },
    ],
  },
  'SWW-TT-DSTAM': {
    familyId: 'SWW-TT-DSTAM',
    defaultExplanation:
      'Als de stam eindigt op -d, krijg je bij hij/zij: d + t = dt. Voorbeeld: hij vindt (stam = vind). Bij "ik" schrijf je alleen de stam: ik vind.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Eindigt de stam op een d? Dan schrijf je bij hij/zij: d + t = dt.\nhij vindt (stam = vind)\nhij wordt (stam = word)\nhij verzendt (stam = verzend)',
      },
      {
        title: 'Ik-vorm: geen t!',
        content:
          'Bij "ik" alleen de stam:\nik vind (niet: ik vindt)\nik word (niet: ik wordt)',
      },
    ],
  },
  'SWW-VT-VZFS': {
    familyId: 'SWW-VT-VZFS',
    defaultExplanation:
      'Let op de v→f en z→s wissel! Bij "verven" is de stam "verf" (f zit in kofschip → verfde). Kijk altijd naar het hele werkwoord, niet alleen de stam.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'V/F-wissel',
        content:
          'Stam is verf, maar het werkwoord is verVen.\nKijk naar de V (niet in kofschip) → verfde\n\nRegel: kijk altijd naar het hele werkwoord!',
      },
      {
        title: 'Z/S-wissel',
        content:
          'Stam is verhuis, maar het werkwoord is verhuiZen.\nZ niet in kofschip → verhuisde',
      },
    ],
  },
  'SWW-VT-DUBBEL': {
    familyId: 'SWW-VT-DUBBEL',
    defaultExplanation:
      'Als de stam al eindigt op -d of -t, krijg je dubbele letters: redden → redde, antwoorden → antwoordde, wachten → wachtte.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Dubbele letters',
        content:
          'Stam op d + de = dubbel d: red → redde\nStam op t + te = dubbel t: wacht → wachtte',
      },
    ],
  },
  'SWW-BN-VD-KORT': {
    familyId: 'SWW-BN-VD-KORT',
    defaultExplanation:
      'Bij een bijvoeglijk naamwoord van een voltooid deelwoord met lange klank laat je een klinker weg: vereren → de vereerde (niet verereerde).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Als het voltooid deelwoord als bijvoeglijk naamwoord wordt gebruikt,\nmaak je het zo kort mogelijk + e:\n\nverbrand → verbrande (auto)\ngered → geredde (jongen, korte klinker → dubbel d)\ngeverfd → geverfde (muur)',
      },
    ],
  },
  'SWW-TT-INV-TRICK': {
    familyId: 'SWW-TT-INV-TRICK',
    defaultExplanation:
      'Pas op: inversie geldt alleen bij jij/je. Bij "Wanneer komt hij?" blijft de -t staan, want "hij" is het onderwerp, niet "jij".',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Jij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"\n"Werk je vandaag?"\n"Vind jij dat ook?"',
      },
      {
        title: 'Let op!',
        content:
          'Dit geldt ALLEEN voor "jij" of "je".\nBij "hij" blijft de t staan: "LoopT hij?"',
      },
    ],
  },
  'SWW-BN-VD-DUBBEL': {
    familyId: 'SWW-BN-VD-DUBBEL',
    defaultExplanation:
      'Bij een korte klinker verdubbel je de medeklinker: gered → de geredde (niet gerede). Bij een lange klinker niet: gepoot → de gepote.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Let op korte klinkers',
        content:
          'Bij een korte klinker verdubbel je de medeklinker:\ngered → geredde (niet: gerede)\ngepot → gepotte (niet: gepote)\n\nBij een lange klinker niet:\ngepoot → gepote (niet: gepotte)\nbesteed → bestede (niet: besteedde)',
      },
    ],
  },
  'SWW-VD-HOMOFOON': {
    familyId: 'SWW-VD-HOMOFOON',
    defaultExplanation:
      'Word of wordt? Gebruik de "smurfen-test": vervang het werkwoord door "smurfen". Klinkt "smurf" goed? Dan geen -t. Klinkt "smurft" goed? Dan wel -t.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Leiden vs. Lijden',
        content:
          'Leiden (ei) = de weg wijzen, aanvoeren\nLijden (ij) = pijn hebben, ondergaan\n\nHij leidt de vergadering.\nHij lijdt aan hoofdpijn.',
      },
      {
        title: 'Word vs. Wordt',
        content:
          'Ik word (stam, geen t)\nHij wordt (stam + t)\nWord jij? (inversie, geen t)',
      },
    ],
  },
  'SWW-LEEN-ENG-STAM': {
    familyId: 'SWW-LEEN-ENG-STAM',
    defaultExplanation:
      'Bij Engelse leenwoorden bepaal je de stam op dezelfde manier: het hele werkwoord min -en. Let op de klank, niet de spelling: racen → race.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel',
      },
    ],
  },
  'SWW-LEEN-ENG-KOF': {
    familyId: 'SWW-LEEN-ENG-KOF',
    defaultExplanation:
      "Bij Engelse leenwoorden pas je 't kofschip toe op de KLANK van de stam. Race (klank s, zit in kofschip) → racete. Download (klank d, niet in kofschip) → downloadde.",
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)",
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Spelling Regels (SPR) — 10 families
  // ══════════════════════════════════════════════════════════
  'SPR-KLANK-JAGER': {
    familyId: 'SPR-KLANK-JAGER',
    defaultExplanation:
      'De jager-regel: in een open lettergreep (ja-ger) schrijf je maar één klinker voor een lange klank. De lettergreep eindigt op een klinker.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Open lettergreep',
        content:
          'Lange klank aan het eind van een klankgroep? Schrijf maar één klinker.\nbo-men (niet boo-men)\nlo-pen (niet loo-pen)\nre-gen (niet ree-gen)',
      },
    ],
  },
  'SPR-KLANK-BAKKER': {
    familyId: 'SPR-KLANK-BAKKER',
    defaultExplanation:
      'De bakker-regel: in een gesloten lettergreep (bak-ker) schrijf je een dubbele medeklinker om de korte klank te behouden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Korte klank? Dan dubbele medeklinker in het meervoud.\npot → pot-ten\nbak → bak-ken\nren → ren-nen',
      },
    ],
  },
  'SPR-WEET-EI-IJ': {
    familyId: 'SPR-WEET-EI-IJ',
    defaultExplanation:
      'Ei of ij? Dit zijn weetwoorden — je moet ze uit je hoofd leren. Tip: de meeste woorden schrijf je met ij. Ei komt vooral voor in: ei, trein, klein, leiden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Sommige woorden schrijf je met -eau.\nVoorbeelden: cadeau, bureau, niveau, plateau',
      },
      {
        title: 'Tip',
        content:
          'Deze woorden komen uit het Frans. Je moet ze uit je hoofd leren!',
      },
    ],
  },
  'SPR-LEEN-C-S-K': {
    familyId: 'SPR-LEEN-C-S-K',
    defaultExplanation:
      'De letter c klinkt als S voor e en i (cent, circus). De c klinkt als K voor a, o en u (cactus, cola). Dit helpt bij het spellen van leenwoorden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'C als S-klank',
        content:
          'C + e/i = S-klank\nVoorbeelden: cent, circus, citroen, cijfer',
      },
      {
        title: 'C als K-klank',
        content:
          'C + a/o/u = K-klank\nVoorbeelden: cola, cactus, computer, cake',
      },
    ],
  },
  'SPR-TREMA-MV': {
    familyId: 'SPR-TREMA-MV',
    defaultExplanation:
      'Een trema (¨) voorkomt dat twee klinkers als één klank worden gelezen. Voorbeeld: zeeën (niet zeeen), ideeën, tweeën.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Meervoud',
        content:
          'Een trema voorkomt dat je twee klinkers als één klank leest.\nideeën (niet ideeen)\nzeeën (niet zeeen)\ntweeën',
      },
      { title: 'Na ge-, be-, ver-', content: 'geëist, geüpload, geëindigd' },
    ],
  },
  'SPR-LEEN-FRANS': {
    familyId: 'SPR-LEEN-FRANS',
    defaultExplanation:
      'Franse leenwoorden hebben bijzondere klankgroepen: -eau (cadeau, bureau), -oir (trottoir), -ique (boutique). Je moet deze uit je hoofd leren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Leenwoorden uit het Frans',
        content:
          'chef (niet sjef)\nroute (niet roete)\ntrottoir (niet trotwaar)\nmiljonair (niet miljonar)\ncaissière',
      },
    ],
  },
  'SPR-SAM-TUSSEN-N': {
    familyId: 'SPR-SAM-TUSSEN-N',
    defaultExplanation:
      'Bij samenstellingen komt er een tussen-n als het eerste deel een meervoud op -en heeft: pannen+koek = pannenkoek. Uitzondering: als er maar één is (zonnebril).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Tussen-n',
        content:
          'Als het eerste deel een meervoud op -en heeft:\npannen + koek = pannenkoek\npaarden + staart = paardenstaart',
      },
      {
        title: 'Uitzondering',
        content:
          'Als er maar één is, geen tussen-n:\nzonnebril (er is maar één zon)\nkoninginnedag (maar: koninginnendag mag ook)',
      },
    ],
  },
  'SPR-LEEN-ENGELS': {
    familyId: 'SPR-LEEN-ENGELS',
    defaultExplanation:
      'Engelse leenwoorden behouden vaak hun Engelse spelling: computer (niet kompjoeter), team (niet tiem). Let op de klankbeelden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Engelse leenwoorden',
        content: 'computer (met c, niet k)\nteam (niet tiem)',
      },
    ],
  },
  'SPR-LEEN-LATIJN': {
    familyId: 'SPR-LEEN-LATIJN',
    defaultExplanation:
      'Woorden uit het Latijn en Grieks hebben bijzondere meervouden: museum → musea, centrum → centra, crisis → crises.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Korte klinker',
        content:
          'Korte klinker → dubbele medeklinker in meervoud.\npan → pannen\nkat → katten',
      },
    ],
  },
  'SPR-ACCENT': {
    familyId: 'SPR-ACCENT',
    defaultExplanation:
      'Een accent kan de betekenis veranderen: vóór (plaats) vs. voor (ten behoeve van), één (telwoord) vs. een (lidwoord).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Woorden met é',
        content:
          'Sommige woorden schrijf je met é (accent aigu).\nVoorbeelden: café, logé',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Getalbegrip (GET) — 12 families
  // ══════════════════════════════════════════════════════════
  'GET-GROOT-POS': {
    familyId: 'GET-GROOT-POS',
    defaultExplanation:
      'In ons talstelsel heeft elk cijfer een positiewaarde. In 78.432: de 7 staat op de tienduizendtallen (70.000), de 8 op de duizendtallen (8.000), enzovoort.',
    defaultVisual: {
      type: 'numberline',
      data: { start: 0, end: 100000, highlight: [78432] },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Kijk goed naar de plek van het cijfer:\nTD - D - H - T - E\n(Tienduizendtallen - Duizendtallen - Honderdtallen - Tientallen - Eenheden)',
      },
      {
        title: 'Voorbeeld: 78.432',
        content:
          '7 = 70.000 (tienduizendtallen)\n8 = 8.000 (duizendtallen)\n4 = 400 (honderdtallen)\n3 = 30 (tientallen)\n2 = 2 (eenheden)',
      },
    ],
  },
  'GET-BREUK-GEL-PLUS': {
    familyId: 'GET-BREUK-GEL-PLUS',
    defaultExplanation:
      'Bij gelijknamige breuken (zelfde noemer) tel je alleen de tellers op. De noemer blijft gelijk. Voorbeeld: 1/4 + 2/4 = 3/4.',
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 3, denominator: 4, label: '3/4', usePizza: true },
      showWhen: 'always',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
        visual: {
          type: 'fraction',
          data: {
            numerator: 3,
            denominator: 4,
            label: 'Driekwart (3/4)',
            visualType: 'pizza',
            usePizza: true,
          },
        },
      },
      {
        title: 'Breuken Optellen',
        content:
          'Bij gelijke noemers: tel alleen de tellers op! De noemer blijft gelijk.\nVoorbeeld: 1/4 + 2/4 = 3/4',
      },
    ],
  },
  'GET-KOMMA-WAARDE': {
    familyId: 'GET-KOMMA-WAARDE',
    defaultExplanation:
      'Bij kommagetallen staan achter de komma de tienden (0,1), honderdsten (0,01) en duizendsten (0,001). Voorbeeld: 3,25 = 3 + 2 tienden + 5 honderdsten.',
    defaultVisual: {
      type: 'numberline',
      data: { start: 0, end: 1, highlight: [0.25, 0.5, 0.75] },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Vermenigvuldigen',
        content:
          'Reken zonder komma, tel daarna het totaal aantal decimalen.\n0,2 × 0,3: 2 × 3 = 6, twee decimalen → 0,06',
      },
      {
        title: 'Delen',
        content:
          'Maak de deler een heel getal door beide met 10 (of 100) te vermenigvuldigen.\n0,6 : 0,2 → 6 : 2 = 3',
      },
    ],
  },
  'GET-PROC-BASIS': {
    familyId: 'GET-PROC-BASIS',
    defaultExplanation:
      'Procent = per honderd. 50% = 50/100 = 0,50 = de helft. 25% = 1/4. 10% = 1/10. Leer deze drieledige koppeling: procent ↔ breuk ↔ decimaal.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-BREUK-ONG-PLUS': {
    familyId: 'GET-BREUK-ONG-PLUS',
    defaultExplanation:
      'Bij ongelijknamige breuken moet je eerst de noemers gelijk maken (KGV). Voorbeeld: 1/3 + 1/4 → 4/12 + 3/12 = 7/12.',
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 7, denominator: 12, label: '7/12', usePizza: true },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
      {
        title: 'Breuken Optellen',
        content:
          'Bij gelijke noemers: tel alleen de tellers op! De noemer blijft gelijk.\nVoorbeeld: 1/4 + 2/4 = 3/4',
      },
    ],
  },
  'GET-BREUK-KEER': {
    familyId: 'GET-BREUK-KEER',
    defaultExplanation:
      'Breuken vermenigvuldigen: teller × teller, noemer × noemer. Voorbeeld: 2/3 × 3/4 = 6/12 = 1/2. Vereenvoudig altijd het antwoord.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'fraction',
      data: {
        numerator: 6,
        denominator: 12,
        label: '6/12 = 1/2',
        usePizza: true,
      },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
    ],
  },
  'GET-PROC-VAN': {
    familyId: 'GET-PROC-VAN',
    defaultExplanation:
      'Percentage van een bedrag: bereken eerst 1% (deel door 100), vermenigvuldig dan met het gevraagde percentage. Of: bereken 10% (deel door 10) en werk daarmee.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Bereken 10% (deel door 10)\nStap 2: Gebruik 10% om het gevraagde percentage te berekenen\n\nVoorbeeld: 40% van 25\n10% = 2,5 → 40% = 4 × 2,5 = 10',
      },
    ],
  },
  'GET-VERH-SCHAAL': {
    familyId: 'GET-VERH-SCHAAL',
    defaultExplanation:
      'Bij een verhoudingstabel zoek je de vermenigvuldigingsfactor. Als 3 → 12 (×4), dan geldt die factor voor de hele rij. Kruislings vermenigvuldigen werkt ook.',
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['A', 'B'],
        rows: [
          [3, 12],
          [5, '?'],
        ],
        highlight: [1, 1],
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel de delen op (bijv. 2:3 = 5 delen)\nStap 2: Deel het totaal door het aantal delen\nStap 3: Vermenigvuldig met het gewenste deel',
      },
    ],
  },
  'GET-PROC-NIEUW': {
    familyId: 'GET-PROC-NIEUW',
    defaultExplanation:
      '25% korting? Dan betaal je 75% (100% - 25%). Bereken 75% van de prijs. 15% opslag? Dan betaal je 115%. Vermenigvuldig met 1,15.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-BREUK-DEEL': {
    familyId: 'GET-BREUK-DEEL',
    defaultExplanation:
      'Delen door een breuk = vermenigvuldigen met het omgekeerde. Voorbeeld: 3/4 ÷ 2/3 = 3/4 × 3/2 = 9/8 = 1 1/8.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'fraction',
      data: { numerator: 9, denominator: 8, label: '9/8', usePizza: true },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Teller en Noemer',
        content:
          'De **teller** (boven) is hoeveel stukken je hebt.\nDe **noemer** (onder) is het totaal aantal stukken.',
      },
    ],
  },
  'GET-PROC-TERUG': {
    familyId: 'GET-PROC-TERUG',
    defaultExplanation:
      'Terugrekenen: als 30% = €60, dan is 1% = €2, dus 100% = €200. Of: deel het bekende bedrag door het percentage en vermenigvuldig met 100.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },
  'GET-PROC-BTW': {
    familyId: 'GET-PROC-BTW',
    defaultExplanation:
      'BTW exclusief berekenen: prijs inclusief ÷ (1 + BTW%). Bij 21% BTW: €121 inclusief → €121 ÷ 1,21 = €100 exclusief.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          '50% = de helft (÷2)\n25% = een kwart (÷4)\n75% = driekwart\n10% = een tiende (÷10)\n1% = een honderdste (÷100)',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Metriek & Meetkunde (MET) — 10 families
  // ══════════════════════════════════════════════════════════
  'MET-LEN-OMREKEN': {
    familyId: 'MET-LEN-OMREKEN',
    defaultExplanation:
      'Gebruik het metriek trapje: km → hm → dam → m → dm → cm → mm. Elke stap naar beneden = ×10, elke stap naar boven = ÷10.',
    defaultVisual: {
      type: 'stairs',
      data: { type: 'length', showAnimation: true },
      showWhen: 'always',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content: 'Gebruik het metriek trapje: km → hm → dam → m → dm → cm → mm',
        visual: {
          type: 'stairs',
          data: { type: 'length', showAnimation: true },
        },
      },
      {
        title: 'Naar beneden = ×10',
        content:
          'Elke stap naar beneden vermenigvuldig je met 10 (een nul erbij).\nVoorbeeld: 3 m = 30 dm = 300 cm = 3.000 mm',
      },
      {
        title: 'Naar boven = ÷10',
        content:
          'Elke stap naar boven deel je door 10 (een nul eraf).\nVoorbeeld: 5.000 mm = 500 cm = 50 dm = 5 m',
      },
    ],
  },
  'MET-OPP-RECHT': {
    familyId: 'MET-OPP-RECHT',
    defaultExplanation:
      'Oppervlakte van een rechthoek = lengte × breedte. Bij een vierkant: zijde × zijde. Eenheid is altijd in het kwadraat (cm², m²).',
    defaultVisual: {
      type: 'geo',
      data: {
        width: 6,
        height: 4,
        unit: 'cm',
        showGrid: true,
        showFormula: true,
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Formule',
        content:
          'Oppervlakte = lengte × breedte\nBij een vierkant: zijde × zijde',
      },
      {
        title: 'Terugrekenen',
        content:
          'Als je de oppervlakte weet en één zijde:\nlengte = oppervlakte ÷ breedte',
      },
    ],
  },
  'MET-INH-BLOK': {
    familyId: 'MET-INH-BLOK',
    defaultExplanation:
      'Inhoud van een balk = lengte × breedte × hoogte. Zorg dat alle maten in dezelfde eenheid staan voordat je rekent.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'stairs',
      data: { type: 'volume', showAnimation: true },
      showWhen: 'wrong',
    },
    hints: [
      {
        content:
          'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
        visual: {
          type: 'stairs',
          data: { type: 'volume', showAnimation: true },
        },
      },
      {
        title: 'Inhoud berekenen',
        content:
          'Inhoud = lengte × breedte × hoogte\nLet op: zet eerst alle maten om naar dezelfde eenheid!',
      },
    ],
  },
  'MET-TIJD-DUUR': {
    familyId: 'MET-TIJD-DUUR',
    defaultExplanation:
      'Tijdsduur berekenen: tel eerst naar het volgende hele uur, dan de hele uren, dan de resterende minuten. Let op: 1 uur = 60 minuten (niet 100!).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel eerst naar het volgende hele uur\nStap 2: Tel dan de hele uren\nStap 3: Tel de resterende minuten',
      },
    ],
  },
  'MET-INH-LITER': {
    familyId: 'MET-INH-LITER',
    defaultExplanation:
      'De sleutelregel: 1 dm³ = 1 liter. Dus inhoud in dm³ is gelijk aan inhoud in liters. 1 m³ = 1.000 liter. 1 cm³ = 1 ml.',
    defaultVisual: {
      type: 'stairs',
      data: { type: 'volume', showAnimation: true },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
        visual: {
          type: 'stairs',
          data: { type: 'volume', showAnimation: true },
        },
      },
    ],
  },
  'MET-OPP-LAND': {
    familyId: 'MET-OPP-LAND',
    defaultExplanation:
      'Landmaten: 1 are = 100 m², 1 hectare = 10.000 m² = 100 are, 1 km² = 100 hectare. Bij oppervlakte gaat elke stap met ×100.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Belangrijk verschil!',
        content:
          'Bij oppervlakte gaat elke stap met ×100 (niet ×10!).\n1 dm² = 100 cm²\n1 m² = 100 dm² = 10.000 cm²\n1 km² = 1.000.000 m²',
      },
    ],
  },
  'MET-INH-CITER': {
    familyId: 'MET-INH-CITER',
    defaultExplanation:
      'Kleine inhoudsmaten: 1 liter = 10 dl = 100 cl = 1.000 ml. cc (kubieke centimeter) = ml. Elke stap is ×10.',
    defaultVisual: {
      type: 'stairs',
      data: { type: 'volume', showAnimation: true },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
        visual: {
          type: 'stairs',
          data: { type: 'volume', showAnimation: true },
        },
      },
    ],
  },
  'MET-MEETK-OMTREK': {
    familyId: 'MET-MEETK-OMTREK',
    defaultExplanation:
      'Omtrek van een cirkel = π × diameter = 2 × π × straal. Gebruik π ≈ 3,14. Voorbeeld: straal 5 cm → omtrek = 2 × 3,14 × 5 = 31,4 cm.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'geo',
      data: {
        width: 6,
        height: 6,
        unit: 'cm',
        showGrid: true,
        showFormula: true,
      },
      showWhen: 'wrong',
    },
    hints: [
      {
        title: 'Formule',
        content: 'Omtrek = 2 × (lengte + breedte)\nBij een vierkant: 4 × zijde',
      },
      {
        content: 'Omtrek = hek (eromheen lopen). Denk aan een hek om een tuin.',
        visual: {
          type: 'rule',
          data: {
            title: 'Ezelsbruggetje',
            content: 'Omtrek = hek om de tuin',
            icon: 'lightbulb',
          },
        },
      },
    ],
  },
  'MET-MEETK-OPP': {
    familyId: 'MET-MEETK-OPP',
    defaultExplanation:
      'Oppervlakte van een cirkel = π × straal². Voorbeeld: straal 5 cm → opp = 3,14 × 25 = 78,5 cm². Let op: straal in het kwadraat!',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'geo',
      data: {
        width: 6,
        height: 6,
        unit: 'cm',
        showGrid: true,
        showFormula: true,
      },
      showWhen: 'wrong',
    },
    hints: [
      {
        title: 'Formule',
        content:
          'Oppervlakte = lengte × breedte\nBij een vierkant: zijde × zijde',
      },
    ],
  },
  'MET-SCHAAL-OPP': {
    familyId: 'MET-SCHAAL-OPP',
    defaultExplanation:
      'Bij schaal en oppervlakte: de schaalfactor geldt in het kwadraat. Schaal 1:100 voor lengte → schaal 1:10.000 voor oppervlakte.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['Kaart', 'Echt'],
        rows: [
          [1, 100],
          ['1 cm²', '10.000 cm²'],
        ],
        highlight: [1, 1],
      },
      showWhen: 'wrong',
    },
    hints: [
      {
        title: 'Belangrijk verschil!',
        content:
          'Bij oppervlakte gaat elke stap met ×100 (niet ×10!).\n1 dm² = 100 cm²\n1 m² = 100 dm² = 10.000 cm²\n1 km² = 1.000.000 m²',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Tijd & Geld (TGL) — 12 families
  // ══════════════════════════════════════════════════════════
  'TGL-TIJD-ANALOOG': {
    familyId: 'TGL-TIJD-ANALOOG',
    defaultExplanation:
      'De kleine wijzer wijst de uren aan, de grote wijzer de minuten. Kwart over = 15 min, half = 30 min, kwart voor = 45 min.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Kleine wijzer = uren\nGrote wijzer = minuten\n\n1 uur = 60 minuten',
      },
      {
        title: 'Kwartieren',
        content:
          'Kwart over = 15 minuten na het hele uur\nHalf = 30 minuten na het hele uur\nKwart voor = 15 minuten voor het volgende uur',
      },
    ],
  },
  'TGL-GELD-TERUG': {
    familyId: 'TGL-GELD-TERUG',
    defaultExplanation:
      'Wisselgeld bereken je door door te tellen vanaf het bedrag dat je betaalt tot wat je geeft. Begin met kleine munten en werk naar boven.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 365,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true,
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Strategie: Doortellen',
        content:
          'Tel door vanaf het bedrag dat je betaalt tot wat je geeft.\n\nVoorbeeld: €6,35 betalen met €10,00\n€6,35 → €6,40 (+5ct) → €6,50 (+10ct) → €7,00 (+50ct) → €10,00 (+€3)\nWisselgeld: €3,65',
      },
    ],
  },
  'TGL-GELD-MAAK': {
    familyId: 'TGL-GELD-MAAK',
    defaultExplanation:
      'Om een bedrag te maken, begin je met de grootste munten of biljetten. Tel steeds op tot je het juiste bedrag hebt. Controleer altijd of je totaal klopt.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 350,
        label: 'Bedrag maken',
        showCoins: true,
        showBills: true,
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Strategie',
        content:
          'Begin met grote munten/biljetten:\n1. Pak eerst €2 of €1 munten\n2. Vul aan met 50 cent\n3. Werk af met kleinere munten (20c, 10c, 5c)\n\nVoorbeeld: €3,75 = €2 + €1 + 50c + 20c + 5c',
      },
      {
        title: 'Controleren',
        content:
          'Tel je munten op:\n€2 = 200 cent\n€1 = 100 cent\n50c = 50 cent\n\nOptellen tot je het juiste totaal hebt!',
      },
    ],
  },
  'TGL-TIJD-DIGITAAL': {
    familyId: 'TGL-TIJD-DIGITAAL',
    defaultExplanation:
      "De digitale klok toont UU:MM (24-uurs notatie). 13:00 = 1 uur 's middags, 00:00 = middernacht. Trek 12 af voor de 'gewone' tijd na 12:00.",
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          "De digitale klok toont: UU:MM\nUU = uren (0-23)\nMM = minuten (0-59)\n\n13:00 = 1 uur 's middags\n14:30 = half drie\n00:00 = middernacht",
      },
    ],
  },
  'TGL-TIJD-ANALOOG-GEDETAILLEERD': {
    familyId: 'TGL-TIJD-ANALOOG-GEDETAILLEERD',
    defaultExplanation:
      'Bij het aflezen van de klok met 5-minuten precisie tel je de streepjes op de klok. Elk streepje is 5 minuten.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Kleine wijzer = uren\nGrote wijzer = minuten\n\nElk groot streepje = 5 minuten\n1 uur = 60 minuten',
      },
      {
        title: 'Minuten tellen',
        content:
          'Tel de grote streepjes vanaf de 12:\n1 streepje = 5 min\n2 streepjes = 10 min\n3 streepjes = 15 min (kwart over)\n6 streepjes = 30 min (half)\n9 streepjes = 45 min (kwart voor)',
      },
    ],
  },
  'TGL-GELD-WISSEL': {
    familyId: 'TGL-GELD-WISSEL',
    defaultExplanation:
      'Wisselgeld bereken je door het verschil te nemen tussen wat je betaalt en wat het kost.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 135,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true,
      },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Methode 1: Aftrekken',
        content:
          'Trek de prijs af van wat je betaalt:\n€5,00 - €3,65 = €1,35 wisselgeld',
      },
      {
        title: 'Methode 2: Doortellen',
        content:
          'Tel door vanaf de prijs:\n€3,65 → €3,70 (+5c) → €4,00 (+30c) → €5,00 (+€1)\nWisselgeld: 5c + 30c + €1 = €1,35',
      },
    ],
  },
  'TGL-TIJD-CONVERSIE': {
    familyId: 'TGL-TIJD-CONVERSIE',
    defaultExplanation:
      'Bij het omzetten van analoog naar digitaal: lees eerst het uur, dan de minuten.',
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        title: 'Analoog → Digitaal',
        content:
          'Stap 1: Lees het uur (kleine wijzer)\nStap 2: Lees de minuten (grote wijzer)\nStap 3: Schrijf als UU:MM',
      },
      {
        title: 'Digitaal → Analoog',
        content:
          'Stap 1: Zet kleine wijzer op het uur\nStap 2: Zet grote wijzer op de minuten',
      },
    ],
  },
  'TGL-GELD-KOMMA': {
    familyId: 'TGL-GELD-KOMMA',
    defaultExplanation:
      "Bij euro/cent notatie: het getal voor de komma zijn euro's, na de komma centen. €3,50 = 3 euro en 50 cent = 350 cent.",
    defaultVisual: {
      type: 'money',
      data: { amount: 350, label: 'Bedrag', showCoins: true, showBills: true },
      showWhen: 'wrong',
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [
      {
        content:
          'Meerdere producten? Tel de prijzen op.\nMeerdere stuks? Vermenigvuldig prijs × aantal.\n\nVoorbeeld: 4 appels van €0,50 = 4 × €0,50 = €2,00',
      },
    ],
  },
  'TGL-TIJD-DUUR': {
    familyId: 'TGL-TIJD-DUUR',
    defaultExplanation:
      'Tijdsduur bereken je door het verschil te nemen tussen begin- en eindtijd. Let op: 1 uur = 60 minuten, niet 100!',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel naar het volgende hele uur\nStap 2: Tel de hele uren\nStap 3: Tel de resterende minuten\n\nVoorbeeld: 14:45 tot 16:20\n→ 15 min (tot 15:00) + 1 uur (tot 16:00) + 20 min = 1u 35min',
      },
      {
        title: 'Let op!',
        content:
          '1 uur = 60 minuten (niet 100!)\n\nAls je meer dan 60 minuten hebt:\n75 minuten = 1 uur en 15 minuten\n90 minuten = 1 uur en 30 minuten',
      },
    ],
  },
  'TGL-GELD-KOOPJES': {
    familyId: 'TGL-GELD-KOOPJES',
    defaultExplanation:
      'Om prijzen te vergelijken, bereken je de prijs per stuk of per kilo.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Prijs per stuk',
        content:
          'Deel de totaalprijs door het aantal stuks:\n\n3 appels voor €1,50 → €1,50 ÷ 3 = €0,50 per appel\n5 appels voor €2,00 → €2,00 ÷ 5 = €0,40 per appel\n\nGoedkoopst: 5 appels voor €2,00',
      },
      {
        title: 'Vergelijken',
        content:
          'Bereken altijd de prijs per eenheid (per stuk, per kg, per liter).\nDe laagste prijs per eenheid is de beste deal!',
      },
    ],
  },
  'TGL-TIJD-PLANNING': {
    familyId: 'TGL-TIJD-PLANNING',
    defaultExplanation: 'Bij tijdplanning reken je vooruit of achteruit.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Vooruit rekenen',
        content:
          'Vertrek + reistijd = aankomst\n\nVoorbeeld: Vertrek 13:40, reis 35 min\n13:40 + 20 min = 14:00\n14:00 + 15 min = 14:15\nAankomst: 14:15',
      },
      {
        title: 'Achteruit rekenen',
        content:
          'Aankomst - reistijd = vertrek\n\nVoorbeeld: Aankomst 15:00, reis 25 min\n15:00 - 25 min = 14:35\nVertrek: 14:35',
      },
    ],
  },
  'TGL-GELD-BUDGET': {
    familyId: 'TGL-GELD-BUDGET',
    defaultExplanation:
      'Bij budgetteren tel je alle uitgaven op en trek je die af van je budget.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Tel alle uitgaven op\nStap 2: Trek het totaal af van je budget\nStap 3: Wat overblijft is je restant\n\nVoorbeeld: Budget €20\nUitgaven: €7,50 + €4,25 = €11,75\nOver: €20 - €11,75 = €8,25',
      },
      {
        title: 'Controleren',
        content:
          'Check altijd:\n- Heb ik genoeg geld?\n- Blijft er iets over?\n- Kan ik dit nog kopen?\n\nAls uitgaven > budget → niet genoeg geld!',
      },
    ],
  },
  'TGL-TIJD-SNELHEID': {
    familyId: 'TGL-TIJD-SNELHEID',
    defaultExplanation:
      'De drie formules: Afstand = snelheid × tijd, Snelheid = afstand ÷ tijd, Tijd = afstand ÷ snelheid.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'De drie formules',
        content:
          'Afstand = snelheid × tijd\nSnelheid = afstand : tijd\nTijd = afstand : snelheid\n\nTip: gebruik de AST-driehoek (Afstand boven, Snelheid en Tijd onder).',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Topografie (TOPO) — 7 families
  // ══════════════════════════════════════════════════════════
  'TOPO-NL-PROV': {
    familyId: 'TOPO-NL-PROV',
    defaultExplanation: 'Nederland heeft 12 provincies.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Leerstrategie',
        content:
          'Begin met de provincies die je kent en werk van daaruit.\nTip: leer ze per regio (noord, oost, zuid, west).',
      },
    ],
  },
  'TOPO-NL-WATER': {
    familyId: 'TOPO-NL-WATER',
    defaultExplanation:
      'Belangrijke wateren: de Rijn, Maas, Waal, IJssel, IJsselmeer, Waddenzee, Noordzee.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Belangrijke wateren',
        content:
          'Rivieren: Rijn, Maas, Waal, IJssel\nMeren: IJsselmeer, Markermeer\nZeeën: Noordzee, Waddenzee',
      },
    ],
  },
  'TOPO-EU-LAND': {
    familyId: 'TOPO-EU-LAND',
    defaultExplanation:
      'Ken de belangrijkste Europese landen en hun hoofdsteden.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Leerstrategie',
        content:
          'Begin met de buurlanden: België, Duitsland, Luxemburg, Frankrijk, Verenigd Koninkrijk.',
      },
    ],
  },
  'TOPO-EU-FYSISCH': {
    familyId: 'TOPO-EU-FYSISCH',
    defaultExplanation:
      'Fysische geografie van Europa: gebergten, rivieren, zeeën.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Overzicht',
        content:
          'Gebergten: Alpen, Pyreneeën, Karpaten\nRivieren: Donau, Rijn, Seine, Wolga\nZeeën: Middellandse Zee, Zwarte Zee, Oostzee',
      },
    ],
  },
  'TOPO-WERELD-CONT': {
    familyId: 'TOPO-WERELD-CONT',
    defaultExplanation: 'Er zijn 7 continenten en 5 oceanen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: '7 Continenten',
        content:
          'Europa, Azië, Afrika, Noord-Amerika, Zuid-Amerika, Oceanië, Antarctica.\n\n5 Oceanen: Stille Oceaan, Atlantische Oceaan, Indische Oceaan, Noordelijke IJszee, Zuidelijke IJszee.',
      },
    ],
  },
  'TOPO-KAART-COOR': {
    familyId: 'TOPO-KAART-COOR',
    defaultExplanation:
      'Coördinaten op een kaart: eerst x, dan y. "Eerst de gang in, dan de trap op".',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Coördinaten lezen',
        content:
          'Eerst de horizontale as (x), dan de verticale as (y).\n\nEzelsbruggetje: "Eerst de gang in, dan de trap op."',
      },
      {
        title: 'Voorbeeld',
        content:
          'Coördinaat (3, 5) betekent:\n→ 3 stappen naar rechts\n→ 5 stappen omhoog',
      },
    ],
  },
  'TOPO-KLIMAAT': {
    familyId: 'TOPO-KLIMAAT',
    defaultExplanation: 'Klimaatzones lopen in banden rond de aarde.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Klimaatzones',
        content:
          'Tropisch: warm en vochtig (rond de evenaar)\nGematigd: wisselende seizoenen (onze zone)\nPolair: koud (bij de polen)',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Taalbegrip (TAA) — 9 families
  // ══════════════════════════════════════════════════════════
  'TAA-ONT-BASIS': {
    familyId: 'TAA-ONT-BASIS',
    defaultExplanation: 'De persoonsvorm vind je door de tijd te veranderen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Hoe vind je het?',
        content:
          'Verander de tijd van de zin. Het woord dat verandert is de persoonsvorm.\n\n"Sophie rent naar school" → "Sophie rende naar school"\nrent → rende, dus "rent" is de persoonsvorm.',
      },
    ],
  },
  'TAA-SOORT-LID': {
    familyId: 'TAA-SOORT-LID',
    defaultExplanation: 'Lidwoorden: de, het, een.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Overzicht',
        content:
          'Zelfstandig naamwoord: mens, dier, ding (de/het ervoor)\nBijvoeglijk naamwoord: hoe is iets? (groot, rood)\nWerkwoord: wat doe je? (lopen, zijn)\nLidwoord: de, het, een',
      },
    ],
  },
  'TAA-LEZEN-VERW': {
    familyId: 'TAA-LEZEN-VERW',
    defaultExplanation:
      'Een verwijswoord verwijst naar iets in een eerdere zin.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content: 'Lees de zin ervoor om te vinden waar het naar verwijst.',
      },
    ],
  },
  'TAA-ONT-LV-MV': {
    familyId: 'TAA-ONT-LV-MV',
    defaultExplanation: 'Lijdend voorwerp: "Wie/wat + pv + onderwerp?"',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Hoe vind je het?',
        content:
          'Vraag: "Wie/wat + persoonsvorm + onderwerp?"\n\nVoorbeeld: De kok bakt een pannenkoek.\nWat bakt de kok? → een pannenkoek = lijdend voorwerp',
      },
    ],
  },
  'TAA-SOORT-VNW': {
    familyId: 'TAA-SOORT-VNW',
    defaultExplanation: 'Voornaamwoorden vervangen een zelfstandig naamwoord.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Overzicht',
        content:
          'ik, jij, hij, zij, wij, jullie, zij, het, dit, dat, wie, wat, mijn, jouw',
      },
    ],
  },
  'TAA-LEZEN-KERN': {
    familyId: 'TAA-LEZEN-KERN',
    defaultExplanation:
      'De hoofdgedachte is het belangrijkste punt van de tekst.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Vraag jezelf: "Als ik de tekst in één zin moet samenvatten, wat zou ik zeggen?"',
      },
    ],
  },
  'TAA-ONT-BWB': {
    familyId: 'TAA-ONT-BWB',
    defaultExplanation:
      'Bijwoordelijke bepalingen geven extra informatie over plaats, tijd, wijze of reden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Hoe vind je het?',
        content: 'Vraag: "Waar?", "Wanneer?", "Hoe?", "Waarom?"',
      },
    ],
  },
  'TAA-LEZEN-FEIT': {
    familyId: 'TAA-LEZEN-FEIT',
    defaultExplanation:
      'Een feit is controleerbaar. Een mening is wat iemand vindt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Feit',
        content: 'Controleerbaar en meetbaar.\nSignalen: cijfers, onderzoek',
      },
      {
        title: 'Mening',
        content:
          'Wat iemand vindt.\nSignalen: "ik vind", "zou moeten", "volgens mij"',
      },
    ],
  },
  'TAA-LEZEN-VERB': {
    familyId: 'TAA-LEZEN-VERB',
    defaultExplanation: 'Signaalwoorden geven het verband aan.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Tegenstelling',
        content: 'maar, toch, hoewel, desondanks, echter',
      },
      {
        title: 'Oorzaak-gevolg',
        content: 'daarom, dus, hierdoor, doordat, daardoor, omdat, want',
      },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // MODULE: Studievaardigheden (STU) — 6 families
  // ══════════════════════════════════════════════════════════
  'STU-ALFABET': {
    familyId: 'STU-ALFABET',
    defaultExplanation: 'Alfabetiseren: sorteer op de eerste letter.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Kijk naar de eerste letter.\nStap 2: Zijn de eerste letters gelijk? Kijk naar de tweede letter.\nStap 3: Ga zo door tot je een verschil vindt.',
      },
    ],
  },
  'STU-TABEL': {
    familyId: 'STU-TABEL',
    defaultExplanation: 'Bij tabellen: lees eerst de kolomkoppen en rijlabels.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['Kolom 1', 'Kolom 2'],
        rows: [
          ['Rij 1', 'Data'],
          ['Rij 2', 'Data'],
        ],
      },
      showWhen: 'always',
    },
    hints: [
      {
        content:
          'Lees de kolomkoppen. Zoek de juiste rij. Vergelijk de getallen.',
      },
    ],
  },
  'STU-GRAFIEK': {
    familyId: 'STU-GRAFIEK',
    defaultExplanation: 'Bij grafieken: lees eerst de titel en aslabels.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'stairs',
      data: { type: 'length', showAnimation: false },
      showWhen: 'always',
    },
    hints: [
      {
        content:
          'Staafdiagram: vergelijk hoeveelheden\nLijndiagram: veranderingen over tijd\nCirkeldiagram: verdeling (altijd 100%)',
      },
    ],
  },
  'STU-KAART': {
    familyId: 'STU-KAART',
    defaultExplanation: 'Bij kaartlezen: gebruik de legenda.',
    requiresHints: false,
    requiresVisual: true,
    defaultVisual: {
      type: 'geo',
      data: { width: 10, height: 10, unit: 'km', showGrid: true },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Kaartlezen',
        content:
          'Stap 1: Lees de legenda.\nStap 2: Bekijk de schaal.\nStap 3: Gebruik het kompas (N = noord = boven).',
      },
    ],
  },
  'STU-BRONNEN': {
    familyId: 'STU-BRONNEN',
    defaultExplanation:
      'Bij bronnen beoordelen: kijk naar wie, wanneer, en met welk doel.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Bronnen beoordelen',
        content:
          '1. Wie heeft dit geschreven?\n2. Wanneer?\n3. Met welk doel?\n4. Feit of mening?\n5. Betrouwbaar?',
      },
    ],
  },
  'STU-COMBINEER': {
    familyId: 'STU-COMBINEER',
    defaultExplanation:
      'Informatie combineren: verzamel gegevens uit meerdere bronnen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Informatie combineren',
        content:
          'Stap 1: Lees elke bron apart.\nStap 2: Zoek gegevens die bij elkaar horen.\nStap 3: Trek een conclusie.',
      },
    ],
  },
}

/**
 * Register or update support content for a family.
 */
export function registerFamilySupport(entry: FamilySupportEntry) {
  if (!entry.familyId) return
  const existing = FAMILY_SUPPORT_REGISTRY[entry.familyId]
  if (existing) {
    FAMILY_SUPPORT_REGISTRY[entry.familyId] = {
      ...existing,
      hints: entry.hints ?? existing.hints,
      defaultVisual: existing.defaultVisual ?? entry.defaultVisual,
      defaultExplanation:
        existing.defaultExplanation ?? entry.defaultExplanation,
      representations: entry.representations ?? existing.representations,
    }
  } else {
    FAMILY_SUPPORT_REGISTRY[entry.familyId] = entry
  }
}

// ============================================================
// CANONICAL HINT RESOLVER
// ============================================================

export function getHintsForFamilyId(familyId: string): HintStep[] | null {
  const entry = FAMILY_SUPPORT_REGISTRY[familyId]
  if (!entry || !entry.hints || entry.hints.length === 0) return null
  return entry.hints
}

export function getFamilySupportEntry(
  familyId: string,
): FamilySupportEntry | null {
  return FAMILY_SUPPORT_REGISTRY[familyId] ?? null
}

export function getAllSupportedFamilyIds(): string[] {
  return Object.keys(FAMILY_SUPPORT_REGISTRY)
}

// ============================================================
// VALIDATION — Hint Coverage
// ============================================================

export interface HintCoverageResult {
  valid: boolean
  totalFamilies: number
  familiesWithHints: number
  coveragePercent: number
  missingHints: string[]
  unknownFamilyIds: string[]
  emptyHints: string[]
}

export function validateHintCoverage(): HintCoverageResult {
  // Lazy import to avoid circular dependency
  const { TAXONOMY } = require('../config/taxonomyRegistry')
  const taxonomyFamilyIds = new Set<string>(
    (TAXONOMY as { familyId: string }[]).map((f) => f.familyId),
  )
  const registryFamilyIds = new Set<string>(
    Object.keys(FAMILY_SUPPORT_REGISTRY),
  )
  const missingHints: string[] = []
  const unknownFamilyIds: string[] = []
  const emptyHints: string[] = []

  for (const familyId of taxonomyFamilyIds) {
    const entry = FAMILY_SUPPORT_REGISTRY[familyId]
    if (!entry) {
      missingHints.push(familyId)
    } else if (!entry.hints || entry.hints.length === 0) {
      emptyHints.push(familyId)
    }
  }

  for (const familyId of registryFamilyIds) {
    if (!taxonomyFamilyIds.has(familyId)) {
      unknownFamilyIds.push(familyId)
    }
  }
  const familiesWithHints = [...taxonomyFamilyIds].filter((id) => {
    const entry = FAMILY_SUPPORT_REGISTRY[id]
    return entry && entry.hints && entry.hints.length > 0
  }).length
  const totalFamilies = taxonomyFamilyIds.size
  return {
    valid: missingHints.length === 0 && unknownFamilyIds.length === 0,
    totalFamilies,
    familiesWithHints,
    coveragePercent:
      totalFamilies > 0
        ? Math.round((familiesWithHints / totalFamilies) * 100)
        : 0,
    missingHints,
    unknownFamilyIds,
    emptyHints,
  }
}

export function assertHintCoverage(): void {
  const result = validateHintCoverage()
  if (!result.valid) {
    logError('🚨 HINT COVERAGE VALIDATION FAILED:')
    if (result.missingHints.length > 0) {
      logError(
        `  ❌ ${result.missingHints.length} families missing hints: ${result.missingHints.slice(0, 10).join(', ')}${result.missingHints.length > 10 ? '...' : ''}`,
      )
    }
    if (result.unknownFamilyIds.length > 0) {
      logError(
        `  ❌ ${result.unknownFamilyIds.length} unknown familyIds in registry: ${result.unknownFamilyIds.join(', ')}`,
      )
    }
  } else {
    debug(
      `✅ Hint Coverage: ${result.familiesWithHints}/${result.totalFamilies} families (${result.coveragePercent}%)`,
    )
    if (result.emptyHints.length > 0) {
      warn(
        `  ⚠️ ${result.emptyHints.length} families have empty hints: ${result.emptyHints.join(', ')}`,
      )
    }
  }
}
